import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, array, ref } from "yup";
import { createUser, resetStatus } from "../redux/usersSagas";
import { useError } from "../hooks/useError";
import { useCascadingFilters } from "../hooks/useCascadingFilters";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import Dropdown from "../../../../components/multiSelectDropdown/multiSelectDropdown";
import ToggleableButton from "../../../../components/toggleableButton/toggleableButton";
import { Input, PasswordInput } from "../../../../components/form/Input/Input";
import { ErrorList } from "../../../../components/ErrorList";
import { generatePassword, passwordSchemaTest } from "../../../../utils/password-utils";
import { showToast } from "../../../../utils/showToast";

const schema = (validateRole) =>
  object({
    first_name: string().label("First Name").trim().min(2).required(),
    last_name: string().label("Last Name").trim().min(2).required(),
    email: string().label("Email").trim().email().required(),
    password: string().label("Password").min(6).max(40).test(passwordSchemaTest).required(),
    password_confirmation: string()
      .label("Password Confirmation")
      .oneOf([ref("password")], "${path} must match Password")
      .required(),
    ...(validateRole ? { role_id: object().required("Please choose role") } : {}),
    location_ids: array().min(1, "Please choose at least 1 location").required()
  }).required();

const generatedPassword = generatePassword();

export const Add = ({ isOpen, onClose }) => {
  const { roles, locations, divisions, districts } = useSelector(
    (state) => state.users.commonSettings
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema(roles.length > 0)),
    defaultValues: {
      divisions: divisions,
      districts: districts,
      location_ids: locations,
      send_welcome_email: true,
      password: generatedPassword,
      password_confirmation: generatedPassword
    }
  });

  const dispatch = useDispatch();

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  const { filteredDistricts, filteredLocations, handleCascadingFilters } = useCascadingFilters({
    districts,
    locations,
    watch,
    setValue
  });

  const { errors: serverErrors, status } = useError(() => {
    showToast("User was successfully created");
    close();
  });

  const primaryButtonDisabled = isSubmitting || status == "pending";
  const onSubmit = async (data) => {
    dispatch(createUser(data));
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        onClose={close}
        parentSelector=".users"
        headerText="Add User"
        headerCloseHandler={close}
        bodyClassName="main-popup-body"
        primaryButtonText="Save"
        primaryButtonDisabled={primaryButtonDisabled}
        primaryBtnHandler={handleSubmit(onSubmit)}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        height="auto"
        width={900}
      >
        <div>
          <div className="users__section">
            <Input label="First Name" htmlFor="first_name" customProps={register("first_name")} />
            <Input label="Last Name" htmlFor="last_name" customProps={register("last_name")} />
            <Input label="Email" htmlFor="email" customProps={register("email")} />
          </div>

          <div className="users__section">
            <PasswordInput label="Password" customProps={register("password")} htmlFor="password" />
            <PasswordInput
              label="Password Confirmation"
              customProps={register("password_confirmation")}
              htmlFor="password_confirmation"
            />

            {roles.length > 0 ? (
              <div className="users__dropdown">
                <p>Role</p>
                <Controller
                  control={control}
                  name="role_id"
                  render={({ field: { onChange, onBlur, value } }) => {
                    return (
                      <Dropdown
                        onBlur={onBlur}
                        height={275}
                        haveOnSelectChange
                        options={roles}
                        selectedFilters={value}
                        numberDisplayed={0}
                        onSelectChange={(data) => onChange(data)}
                      />
                    );
                  }}
                />
              </div>
            ) : (
              <div className="users__empty" />
            )}
          </div>

          <div className="users__section">
            <div className="users__dropdown">
              <p>Divisions</p>
              <Controller
                control={control}
                name="divisions"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Dropdown
                      includeSelectAll
                      height={275}
                      haveOnSelectChange
                      enableOnly
                      options={divisions}
                      selectedFilters={value ?? []}
                      numberDisplayed={0}
                      onSelectChange={(data) => handleCascadingFilters(data, onChange, "divisions")}
                    />
                  );
                }}
              />
            </div>

            <div className="users__dropdown">
              <p>Districts</p>
              <Controller
                control={control}
                name="districts"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Dropdown
                      includeFilter={filteredDistricts.length > 1}
                      includeSelectAll={filteredDistricts.length > 1}
                      key={filteredDistricts.length}
                      height={275}
                      haveOnSelectChange
                      enableOnly
                      options={filteredDistricts}
                      selectedFilters={value ?? []}
                      numberDisplayed={0}
                      onSelectChange={(data) => handleCascadingFilters(data, onChange, "districts")}
                    />
                  );
                }}
              />
            </div>

            <div className="users__dropdown">
              <p>Locations</p>
              <Controller
                control={control}
                name="location_ids"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Dropdown
                      includeFilter={filteredLocations.length > 1}
                      includeSelectAll={filteredLocations.length > 1}
                      key={filteredLocations.length}
                      height={275}
                      haveOnSelectChange
                      enableOnly
                      options={filteredLocations}
                      selectedFilters={value ?? []}
                      numberDisplayed={0}
                      onSelectChange={(data) => onChange(data)}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="users__section">
            <div className="switch">
              <p className="switch__title">Send Welcome Email</p>
              <Controller
                control={control}
                name="send_welcome_email"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="showWelcomeEmail"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>
          </div>

          {status === "pending" && <LoadingAnimation uiLoader />}
          <ErrorList errors={{ ...errors, ...serverErrors }} />
        </div>
      </MainModal>
    </>
  );
};
